import { Component, computed, effect, EventEmitter, inject, input, output, Output, signal } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { Regard3000Configuration } from "@domain/project/configurations/regard-3000-configuration";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { Regard3000Service } from "@project/gas-warning-center-configuration/regard-3000/regard-3000.service";

type ModuleType = "input" | "relay" | "gateway";

@Component({
  selector: "app-gas-warning-center-module-selector",
  imports: [UiKitModule, FilterProductComponent, ProductCardWithQuantityComponent, RefreshProductsHintComponent],
  templateUrl: "./module-selector.component.html",
  styleUrl: "./module-selector.component.scss",
  standalone: true,
})
export class ModuleSelectorComponent {
  private productService = inject(ProductService);
  private regard3000Service = inject(Regard3000Service);

  readonly showCosts = input.required<boolean>();
  readonly originalRegard3000Configuration = input<Regard3000Configuration>();
  readonly type = input<GasWarningCenterType>();

  readonly selectedInputModulesChange = output<ProductWithQuantity[]>();
  readonly selectedRelayModulesChange = output<ProductWithQuantity[]>();
  readonly selectedGatewayModulesChange = output<ProductWithQuantity[]>();

  protected inputModules = signal<Product[]>([]);
  protected relayModules = signal<Product[]>([]);
  protected gatewayModules = signal<Product[]>([]);
  readonly selectionMode: "single" | "multi" = "multi";
  private selectedInputModules = signal<ProductWithQuantity[]>([]);
  private selectedRelayModules = signal<ProductWithQuantity[]>([]);
  private selectedGatewayModules = signal<ProductWithQuantity[]>([]);
  readonly searchQuery = signal<string>("");

  readonly filteredInputModules = computed(() => {
    return this.filterModules(this.inputModules());
  });

  readonly filteredRelayModules = computed(() => {
    return this.filterModules(this.relayModules());
  });

  readonly filteredGatewayModules = computed(() => {
    return this.filterModules(this.gatewayModules());
  });

  private filterModules(modules: Product[]) {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return modules;

    return modules.filter((module) => {
      const name = module.name?.toLowerCase() ?? "";
      const id = String(module.id ?? "").toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }

  @Output() modulesSelected$ = new EventEmitter<{
    inputModules: ProductWithQuantity[];
    relayModules: ProductWithQuantity[];
    gatewayModules: ProductWithQuantity[];
  }>();

  constructor() {
    effect(() => {
      const selectedInputModules = this.selectedInputModules();
      const selectedRelayModules = this.selectedRelayModules();
      const selectedGatewayModules = this.selectedGatewayModules();
      this.selectedInputModulesChange.emit(selectedInputModules);
      this.selectedRelayModulesChange.emit(selectedRelayModules);
      this.selectedGatewayModulesChange.emit(selectedGatewayModules);
    });

    effect((onCleanup) => {
      const type = this.type();
      if (!type) return;

      this.inputModules.set([]);
      this.relayModules.set([]);
      this.gatewayModules.set([]);
      this.selectedInputModules.set([]);
      this.selectedRelayModules.set([]);
      this.selectedGatewayModules.set([]);

      const subscription = this.regard3000Service
        .getModuleProducts(type)
        .subscribe(({ inputModules, relayModules, gatewayModules }) => {
          this.inputModules.set(this.uniqueProducts(inputModules ?? []));
          this.relayModules.set(this.uniqueProducts(relayModules ?? []));
          this.gatewayModules.set(this.uniqueProducts(gatewayModules ?? []));
        });

      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      const type = this.type();
      const originalConfiguration = this.originalRegard3000Configuration();
      if (type && originalConfiguration) {
        if (originalConfiguration.inputModules) {
          for (const module of originalConfiguration.inputModules) {
            this.productService.getProductById(module.productId).subscribe((product) => {
              if (product) {
                this.updateSelectedModule(module.quantity, product, "input");
              }
            });
          }
        }
        if (originalConfiguration.relayModules) {
          for (const module of originalConfiguration.relayModules) {
            this.productService.getProductById(module.productId).subscribe((product) => {
              if (product) {
                this.updateSelectedModule(module.quantity, product, "relay");
              }
            });
          }
        }

        if (originalConfiguration.gatewayModules) {
          for (const module of originalConfiguration.gatewayModules) {
            this.productService.getProductById(module.productId).subscribe((product) => {
              if (product) {
                this.updateSelectedModule(module.quantity, product, "gateway");
              }
            });
          }
        }
      }
    });
  }

  protected search(query: string) {
    this.searchQuery.set(query ?? "");
  }

  protected getSelectedModuleQuantity(module: Product, type: ModuleType): number {
    const selectedModulesMap: Record<ModuleType, ProductWithQuantity[]> = {
      input: this.selectedInputModules(),
      relay: this.selectedRelayModules(),
      gateway: this.selectedGatewayModules(),
    };
    return selectedModulesMap[type].find((selectedModule) => selectedModule.product.id === module.id)?.quantity ?? 0;
  }

  protected updateSelectedModule(quantity: number, selectedModule: Product, type: ModuleType): void {
    if (!selectedModule?.id) return;

    const signal = this.getModulesSignal(type);

    signal.update((list) => {
      const index = list.findIndex((module) => module.product.id === selectedModule.id);

      if (quantity <= 0) {
        if (index === -1) return list;
        return list.filter((_, i) => i !== index);
      }

      if (index === -1) {
        return [...list, { product: selectedModule, quantity: Math.floor(quantity) }];
      }

      const updated = [...list];
      updated[index] = { ...updated[index], quantity: Math.floor(quantity) };
      return updated;
    });
  }

  private getModulesSignal(type: ModuleType) {
    switch (type) {
      case "input":
        return this.selectedInputModules;
      case "relay":
        return this.selectedRelayModules;
      case "gateway":
        return this.selectedGatewayModules;
      default:
        throw new Error(`Unknown ModuleType: ${type}`);
    }
  }

  private uniqueProducts(products: Product[]): Product[] {
    return [...new Map(products.map((product) => [product.id, product])).values()];
  }
}
